import i18n from '@/utils/i18n';
import { DisplayConfigCategory } from '@/types';
import {
  displayConfigToRow,
  ItemDetailSectionConfig,
  ItemDetailSectionRow,
} from '@/experiences/sales-portal/domain/items/components/item-detail-section';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { ReactNode, useMemo } from 'react';
import { useOrderEntryLabels } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-labels';

export interface UseGeneralTabSectionsArgs {
  data: ItemDetailContentData;
  grossProfitRowDetail?: ReactNode;
  isGrossProfitHidden: boolean;
  isSalesRepInfoDisabled: boolean;
  labels: OrderEntryLabel | undefined;
  omitPriceAndGrossProfitRows?: boolean;
  orderGuideLabels: { level: string; orderGuideName: string }[];
  priceRowDetail?: ReactNode;
}

/**
 * Derives the General tab's card sections (Status / Pricing info / Purchase
 * information / Custom info) from the item-detail payload and the supplier's
 * display configs.
 *
 * @param data - Combined panel data from {@link useItemDetailContentLoader}
 * @param grossProfitRowDetail - Rendered beneath the GP% value (price override indicator)
 * @param isGrossProfitHidden - Hides GP% fields (supplier-wide GP display config)
 * @param isSalesRepInfoDisabled - Hides the pricing info section (sales-rep visibility rules)
 * @param labels - Row labels (availability / essential / lead time) for the Status card
 * @param omitPriceAndGrossProfitRows - Omits the Price and GP% rows (shown as editable fields elsewhere)
 * @param orderGuideLabels - Order guides the row belongs to
 * @param priceRowDetail - Rendered beneath the Price value (price override indicator)
 * @returns Section configs for {@link ItemDetailSection}
 */
export const useGeneralTabSections = ({
  data,
  grossProfitRowDetail,
  isGrossProfitHidden,
  isSalesRepInfoDisabled,
  labels,
  omitPriceAndGrossProfitRows,
  orderGuideLabels,
  priceRowDetail,
}: UseGeneralTabSectionsArgs): ItemDetailSectionConfig[] => {
  const defaultGuideName = i18n.t('sales_portal.guide_selector.default');
  const filteredOrderGuideLabels = useMemo(
    () =>
      orderGuideLabels.filter(
        (label) => label.orderGuideName !== defaultGuideName,
      ),
    [orderGuideLabels, defaultGuideName],
  );
  const statusBadges = useOrderEntryLabels(labels, filteredOrderGuideLabels);

  return useMemo<ItemDetailSectionConfig[]>(() => {
    const rowBuckets = data.displayConfigs.reduce<{
      custom: ItemDetailSectionRow[];
      inventory: ItemDetailSectionRow[];
      pricing: ItemDetailSectionRow[];
    }>(
      (acc, config) => {
        const row = displayConfigToRow(config);
        if (config.category === DisplayConfigCategory.Pricing) {
          acc.pricing.push(row);
        } else if (config.category === DisplayConfigCategory.Inventory) {
          acc.inventory.push(row);
        } else {
          acc.custom.push(row);
        }
        return acc;
      },
      { custom: [], inventory: [], pricing: [] },
    );

    const pricingRows: ItemDetailSectionRow[] = [
      ...(omitPriceAndGrossProfitRows
        ? []
        : [
            {
              detail: priceRowDetail,
              key: 'price',
              label: i18n.t(
                'sales_portal.order_entry.item_detail.pricing.price',
              ),
              value: data.pricingSummary.priceDisplay || null,
            },
          ]),
      {
        key: 'unit_price',
        label: i18n.t(
          'sales_portal.order_entry.item_detail.pricing.unit_price',
        ),
        value: data.pricingSummary.unitPriceDisplay || null,
      },
      ...(isGrossProfitHidden || omitPriceAndGrossProfitRows
        ? []
        : [
            {
              detail: grossProfitRowDetail,
              key: 'gp',
              label: i18n.t(
                'sales_portal.order_entry.item_detail.pricing.gp_percent',
              ),
              value: data.pricingSummary.gpDisplay || null,
            },
          ]),
      ...rowBuckets.pricing,
    ];

    return [
      {
        content: { badges: statusBadges, kind: 'badges' },
        key: 'status',
        title: i18n.t('sales_portal.order_entry.item_detail.status.title'),
      },
      {
        content: { kind: 'rows', rows: pricingRows },
        hidden: isSalesRepInfoDisabled,
        hideWhenEmpty: false,
        key: 'pricing',
        title: i18n.t(
          'sales_portal.order_entry.item_detail.pricing_info.title',
        ),
      },
      {
        content: { kind: 'rows', rows: rowBuckets.inventory },
        key: 'inventory',
        title: i18n.t(
          'sales_portal.order_entry.item_detail.purchase_info.title',
        ),
      },
      {
        content: { kind: 'rows', rows: rowBuckets.custom },
        key: 'custom',
        title: i18n.t('sales_portal.order_entry.item_detail.custom_info.title'),
      },
    ];
  }, [
    data.displayConfigs,
    data.pricingSummary.gpDisplay,
    data.pricingSummary.priceDisplay,
    data.pricingSummary.unitPriceDisplay,
    grossProfitRowDetail,
    isGrossProfitHidden,
    isSalesRepInfoDisabled,
    omitPriceAndGrossProfitRows,
    priceRowDetail,
    statusBadges,
  ]);
};
